import { z } from 'zod'

/**
 * Sobre de deep links que acompaña a los payloads.
 *
 * Decisión: **los deep links los arma el servidor**, no el front. La regla
 * "si falta el id externo no se renderiza un link roto" vive en un solo lugar
 * (`deeplinks.ts`) y el front solo tiene que distinguir `null` de objeto.
 *
 * Van en un sobre aparte y no dentro de cada entidad porque las entidades ya
 * están definidas en el contrato (`Client`, `Followup`, …) y meterles un campo
 * nuevo obligaría a versionarlas. El sobre se indexa por el id de la entidad:
 * `links.clientes['cli-armytech'].stripeCustomer`.
 *
 * Un valor `null` significa exactamente una cosa: **sin vincular**. La UI
 * muestra el estado "sin vincular" con la acción de vincularlo, igual que un
 * KPI sin dato. Nunca un `<a>` a una URL incompleta.
 */

export const deepLinkPayloadSchema = z.object({
  system: z.enum([
    'plane',
    'close',
    'stripe',
    'plataforma',
    'search_console',
    'langfuse',
  ]),
  url: z.string().url(),
  label: z.string(),
  systemLabel: z.string(),
})
export type DeepLinkPayload = z.infer<typeof deepLinkPayloadSchema>

const link = deepLinkPayloadSchema.nullable()

/** Links de una cuenta: su lead en Close, su customer y suscripción en Stripe, su ficha en la plataforma. */
export const clientLinksSchema = z.object({
  close: link,
  stripeCustomer: link,
  stripeSubscription: link,
  plataforma: link,
})
export type ClientLinks = z.infer<typeof clientLinksSchema>

/**
 * Por qué un link vino en `null`.
 *
 * `null` significa "sin vincular", pero no siempre falta lo mismo, y el pedido
 * que arma la UI cambia según el caso:
 *
 *  - `id_del_registro`: falta el id externo **de ese registro** (el lead de
 *    Armytech en Close). El pedido es por fila y hay tantos como filas.
 *  - `config_global`: falta **una variable de entorno** que gobierna a todas las
 *    filas de la sección. Es el caso de `SEARCH_CONSOLE_RESOURCE_ID`: quince
 *    queries de SEO sin link por un solo dato que falta cargar una vez. Sin
 *    esto la UI pide quince cosas distintas que en realidad son una sola.
 *
 * Cuando el motivo no viaja (campo ausente o `null`) el estado es el de
 * siempre: "sin vincular" a secas.
 */
export const motivoSinVincularSchema = z.object({
  tipo: z.enum(['id_del_registro', 'config_global']),
  /** Variable que falta cargar, cuando `tipo` es 'config_global'. */
  envVar: z.string().nullable().default(null),
  /** Qué falta, dicho para una persona. Va al estado y al pedido. */
  mensaje: z.string(),
  /** A dónde ir a resolverlo dentro del panel. null = no hay pantalla. */
  accion: z
    .object({ label: z.string(), ruta: z.string() })
    .nullable()
    .default(null),
})
export type MotivoSinVincular = z.infer<typeof motivoSinVincularSchema>

export const followupLinksSchema = z.object({ plane: link })
export type FollowupLinks = z.infer<typeof followupLinksSchema>

/**
 * Links de un empleado digital. Su ficha vive en la plataforma; su consumo, en
 * Langfuse.
 *
 * `langfuse` y `sinVincular` son opcionales a propósito: los módulos que no
 * hablan de consumo (la ficha de cliente, la brecha de activación) arman el
 * sobre solo con `plataforma` y siguen siendo válidos. Ausente se lee igual que
 * `null` — "sin vincular" a secas.
 */
export const agenteLinksSchema = z.object({
  plataforma: link,
  /** Sesiones del agente en Langfuse, con el período ya filtrado. */
  langfuse: link.optional(),
  sinVincular: motivoSinVincularSchema.nullable().optional(),
})
export type AgenteLinks = z.infer<typeof agenteLinksSchema>

/** Links de un modelo: su uso en Langfuse durante el período. */
export const modeloLinksSchema = z.object({
  langfuse: link,
  sinVincular: motivoSinVincularSchema.nullable().optional(),
})
export type ModeloLinks = z.infer<typeof modeloLinksSchema>

export const seoLinksSchema = z.object({
  searchConsole: link,
  /**
   * Por qué no hay link. Hoy siempre es el mismo: la propiedad de Search
   * Console no está configurada, así que la explicación es una y la acción es
   * ir a cargarla, no "vincular esta query".
   *
   * Opcional a propósito: un sobre armado a mano (fixtures, un endpoint que
   * todavía no lo calcula) sigue siendo válido, y ausente se lee igual que
   * `null` — "sin vincular" a secas, sin motivo.
   */
  sinVincular: motivoSinVincularSchema.nullable().optional(),
})
export type SeoLinks = z.infer<typeof seoLinksSchema>

/** La oportunidad vive dentro de la página del lead: viajan los dos links. */
export const oportunidadLinksSchema = z.object({
  close: link,
  oportunidad: link,
})
export type OportunidadLinks = z.infer<typeof oportunidadLinksSchema>

export const linksSchema = z.object({
  /** Por id de cliente. */
  clientes: z.record(z.string(), clientLinksSchema).default({}),
  /** Por id de seguimiento. */
  followups: z.record(z.string(), followupLinksSchema).default({}),
  /** Por id de agente (empleado digital). */
  agentes: z.record(z.string(), agenteLinksSchema).default({}),
  /** Por id de modelo del catálogo. */
  modelos: z.record(z.string(), modeloLinksSchema).default({}),
  /** Por texto exacto de la query: es su clave en Search Console. */
  seoQueries: z.record(z.string(), seoLinksSchema).default({}),
  /** Por id de oportunidad del funnel. */
  oportunidades: z.record(z.string(), oportunidadLinksSchema).default({}),
})
export type Links = z.infer<typeof linksSchema>

export const EMPTY_LINKS: Links = {
  clientes: {},
  followups: {},
  agentes: {},
  modelos: {},
  seoQueries: {},
  oportunidades: {},
}
